import React from 'react';
import { Calendar } from '../ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Lock } from 'lucide-react';

const fmt = (d) =>
  d
    ? d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

const diffNights = (a, b) => {
  if (!a || !b) return 0;
  const ms = b.setHours(0, 0, 0, 0) - a.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round(ms / 86400000));
};

/**
 * DateRangePicker
 * Modes:
 *   - stayType="nightly" (default): standard range picker, user picks both dates.
 *   - stayType="weekly" or "monthly" with lockedDuration set: user picks ONLY the
 *     check-in date, check-out is auto-filled by the parent. Calendar single-date
 *     mode keeps it simple; we still highlight the implied range visually via
 *     `selected` prop.
 */
export const DateRangePicker = ({
  checkIn,
  checkOut,
  onCheckIn,
  onCheckOut,
  stayType = 'nightly',
  lockedDuration = null,
}) => {
  const isLocked = !!lockedDuration && stayType !== 'nightly';

  // Range form (used for both display and for range-select mode)
  const range = {
    from: checkIn || undefined,
    to: checkOut || undefined,
  };

  const handleRangeSelect = (next) => {
    onCheckIn(next?.from || null);
    onCheckOut(next?.to || null);
  };

  const handleSingleSelect = (date) => {
    // For locked-duration stays, only the check-in date is user-selected.
    // Parent's useEffect will compute and set check-out.
    if (!date) {
      onCheckIn(null);
      onCheckOut(null);
      return;
    }
    onCheckIn(date);
  };

  const nights =
    checkIn && checkOut ? diffNights(new Date(checkIn), new Date(checkOut)) : 0;

  return (
    <Card
      data-testid="date-range-card"
      className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B]"
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-2xl text-[#F4F1EA]">Select Dates</CardTitle>
            <p className="text-sm text-[#F4F1EA]/70 mt-1">
              {isLocked
                ? `Pick your check-in date — check-out auto-fills ${lockedDuration} ${
                    lockedDuration === 1 ? 'night' : 'nights'
                  } later.`
                : 'Click your check-in date, then click your check-out date to set the range.'}
            </p>
          </div>
          {isLocked && (
            <span
              data-testid="stay-type-lock-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#B8860B]/15 text-[#E5C158] border border-[#B8860B]/40 capitalize"
            >
              <Lock size={12} />
              {stayType} · {lockedDuration} nights
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-[1fr_240px] gap-6 items-start">
          <div
            data-testid="check-in-calendar"
            className="rounded-md border border-[#B8860B]/30 bg-[#050814] text-[#F4F1EA] p-2 flex justify-center"
          >
            {isLocked ? (
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={range}
                onDayClick={handleSingleSelect}
                disabled={(date) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0))
                }
                data-testid="check-out-calendar"
              />
            ) : (
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={range}
                onSelect={handleRangeSelect}
                disabled={(date) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0))
                }
                data-testid="check-out-calendar"
              />
            )}
          </div>

          {/* Trip summary panel */}
          <div className="rounded-md border border-[#B8860B]/30 bg-[#050814] p-4 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#B8860B]/80 mb-1">
                Check-in
              </p>
              <p
                data-testid="trip-checkin-display"
                className="text-[#F4F1EA] font-semibold"
              >
                {fmt(checkIn)}
              </p>
            </div>
            <div className="h-px bg-[#B8860B]/15" />
            <div>
              <p className="text-xs uppercase tracking-wider text-[#B8860B]/80 mb-1">
                Check-out
                {isLocked && (
                  <span className="ml-1.5 text-[10px] text-[#F4F1EA]/50">(auto)</span>
                )}
              </p>
              <p
                data-testid="trip-checkout-display"
                className="text-[#F4F1EA] font-semibold"
              >
                {fmt(checkOut)}
              </p>
            </div>
            <div className="h-px bg-[#B8860B]/15" />
            <div className="flex items-center justify-between pt-1">
              <p className="text-xs uppercase tracking-wider text-[#B8860B]/80">
                Nights
              </p>
              <p
                data-testid="trip-nights-display"
                className="text-2xl font-bold text-[#B8860B]"
              >
                {nights}
              </p>
            </div>
            {!isLocked && checkIn && !checkOut && (
              <p className="text-xs text-[#F4F1EA]/60 pt-1">
                Now click your check-out date.
              </p>
            )}
            {isLocked && !checkIn && (
              <p className="text-xs text-[#F4F1EA]/60 pt-1">
                Pick any date — we'll lock in your {lockedDuration}-night stay.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
